import { useCallback, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import { ChatInput } from "@bastion/assistant";
import { TOUCH_QUERY, useMediaQuery } from "@bastion/ui";
import { usePageVisits, type PageVisit } from "@/app/layout/usePageVisits";
import { CAPABILITY_PROMPTS } from "@/shared/assistant/capabilityPrompts";
import styles from "./HomePage.module.css";

/** Rows per list. */
const SHOWN = 6;

/** The pages a new browser starts with, before it has any history. */
const STARTERS: Array<Pick<PageVisit, "path" | "label" | "context">> = [
  { path: "/episodes", label: "Episodes", context: "Data" },
  { path: "/datasets", label: "Datasets", context: "Data" },
  { path: "/fleet/robots", label: "Robots", context: null },
  { path: "/ml-studio/policies", label: "Policies", context: "ML" },
];

function ago(at: number, now: number): string {
  const minutes = Math.round((now - at) / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function Shortcuts({
  title,
  rows,
  aside,
}: {
  title: string;
  rows: Array<Pick<PageVisit, "path" | "label" | "context">>;
  aside: (row: Pick<PageVisit, "path" | "label" | "context">) => string;
}) {
  return (
    <section className={styles.group} aria-label={title}>
      <h2 className={styles.groupTitle}>{title}</h2>
      <ul className={styles.list}>
        {rows.map((row) => (
          <li key={row.path}>
            <RouterLink to={row.path} className={styles.row}>
              <span className={styles.label}>{row.label}</span>
              {row.context ? <span className={styles.context}>{row.context}</span> : null}
              <span className={styles.aside}>{aside(row)}</span>
            </RouterLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Home: shortcuts to the pages this browser opened most recently and most
 * often. At the bottom, "How can I help?", the chat box and four suggested
 * prompts. Sending a message or a prompt starts a chat at /chat/:id.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const visits = usePageVisits();
  // On a touch screen the field does not take focus at load, so the
  // keyboard does not open over the page.
  const isTouch = useMediaQuery(TOUCH_QUERY);
  const now = Date.now();

  const recent = visits.slice(0, SHOWN);
  const frequent = useMemo(
    () => [...visits].sort((a, b) => b.count - a.count || b.lastAt - a.lastAt).slice(0, SHOWN),
    [visits],
  );

  const startChat = useCallback(
    (prompt: string) => {
      navigate(`/chat/${crypto.randomUUID()}`, { state: { initialMessage: prompt } });
    },
    [navigate],
  );

  return (
    <div className={styles.page} data-testid="page-home">
      <div className={styles.center}>
        {visits.length === 0 ? (
          <Shortcuts title="Start with" rows={STARTERS} aside={() => ""} />
        ) : (
          <div className={styles.columns}>
            <Shortcuts
              title="Recent"
              rows={recent}
              aside={(row) => ago((row as PageVisit).lastAt, now)}
            />
            <Shortcuts
              title="Most visited"
              rows={frequent}
              aside={(row) => `${(row as PageVisit).count}×`}
            />
          </div>
        )}
      </div>
      <div className={styles.composer}>
        <h2 className={styles.title}>How can I help?</h2>
        <ChatInput
          variant="hero"
          autoFocus={!isTouch}
          onSend={startChat}
          placeholder="Ask anything…"
        />
        <div className={styles.suggestions} role="group" aria-label="Suggested prompts">
          {CAPABILITY_PROMPTS.map((item) => (
            <button
              key={item.label}
              type="button"
              className={styles.suggestion}
              onClick={() => startChat(item.prompt)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
